import React, { createContext, useCallback, useRef, useState } from 'react';
import ToastContainer from '../components/ToastContainer';
import type { Toast, ToastAction, ToastSeverity, ToastContextValue } from './ToastContext.types';

export type { ToastSeverity, Toast, ToastAction } from './ToastContext.types';

// eslint-disable-next-line react-refresh/only-export-components -- context must co-locate with provider
export const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [toastRightOffset, setToastRightOffset] = useState(0);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const addToast = useCallback(
    (message: string, severity: ToastSeverity, duration = 5000, action?: ToastAction) => {
      const id = `${Date.now()}-${Math.random()}`;
      const toast: Toast = { id, message, severity, duration, action };

      setToasts((prev) => {
        const next = [...prev, toast];
        if (next.length > 5) {
          const removed = next.splice(0, next.length - 5);
          removed.forEach((t) => {
            const timer = timers.current.get(t.id);
            if (timer !== undefined) {
              clearTimeout(timer);
              timers.current.delete(t.id);
            }
          });
        }
        return next;
      });

      const timer = setTimeout(() => removeToast(id), duration);
      timers.current.set(id, timer);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast, toasts, toastRightOffset, setToastRightOffset }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};
